import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  User,
  updateProfile,
  sendEmailVerification,
  sendPasswordResetEmail
} from 'firebase/auth';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { auth, db } from './config';

export interface UserData {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
  cars: Car[];
  isEnrolled: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface Car {
  id: string;
  make: string;
  model: string;
  year: number;
  spec: string;
  photos: string[];
  createdAt: Date;
  updatedAt: Date;
}

// Register new user
export const registerUser = async (
  email: string,
  password: string,
  firstName: string,
  lastName: string
): Promise<UserData> => {
  try {
    // Create user account
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;

    // Update display name
    await updateProfile(user, {
      displayName: `${firstName} ${lastName}`
    });

    // Send email verification
    await sendEmailVerification(user);

    // Create user document in Firestore
    const userData: UserData = {
      id: user.uid,
      email: user.email!,
      firstName,
      lastName,
      phone: '',
      cars: [],
      isEnrolled: false,
      createdAt: new Date(),
      updatedAt: new Date()
    };

    await setDoc(doc(db, 'users', user.uid), userData);

    return userData;
  } catch (error: any) {
    throw new Error(error.message);
  }
};

// Sign in user
export const signInUser = async (email: string, password: string): Promise<UserData> => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;

    // Get user data from Firestore
    const userDoc = await getDoc(doc(db, 'users', user.uid));
    
    if (!userDoc.exists()) {
      throw new Error('User data not found');
    }

    return userDoc.data() as UserData;
  } catch (error: any) {
    throw new Error(error.message);
  }
};

// Sign out user
export const signOutUser = async (): Promise<void> => {
  try {
    await signOut(auth);
  } catch (error: any) {
    throw new Error(error.message);
  }
};

// Get current user data
export const getCurrentUserData = async (uid: string): Promise<UserData | null> => {
  try {
    const userDoc = await getDoc(doc(db, 'users', uid));
    
    if (!userDoc.exists()) {
      return null;
    }

    return userDoc.data() as UserData;
  } catch (error: any) {
    throw new Error(error.message);
  }
};

// Update user data
export const updateUserData = async (uid: string, userData: Partial<UserData>): Promise<void> => {
  try {
    await setDoc(doc(db, 'users', uid), {
      ...userData,
      updatedAt: new Date()
    }, { merge: true });
  } catch (error: any) {
    throw new Error(error.message);
  }
};

// Add car to user
export const addCarToUser = async (uid: string, car: Omit<Car, 'id' | 'createdAt' | 'updatedAt'>): Promise<void> => {
  try {
    const userDoc = await getDoc(doc(db, 'users', uid));
    
    if (!userDoc.exists()) {
      throw new Error('User not found');
    }

    const userData = userDoc.data() as UserData;
    const newCar: Car = {
      ...car,
      id: Date.now().toString(),
      createdAt: new Date(),
      updatedAt: new Date()
    };

    await updateUserData(uid, {
      cars: [...userData.cars, newCar]
    });
  } catch (error: any) {
    throw new Error(error.message);
  }
};

// Update car
export const updateCar = async (uid: string, carId: string, carData: Partial<Car>): Promise<void> => {
  try {
    const userDoc = await getDoc(doc(db, 'users', uid));
    
    if (!userDoc.exists()) {
      throw new Error('User not found');
    }

    const userData = userDoc.data() as UserData;
    const updatedCars = userData.cars.map(car => 
      car.id === carId 
        ? { ...car, ...carData, updatedAt: new Date() }
        : car
    );

    await updateUserData(uid, { cars: updatedCars });
  } catch (error: any) {
    throw new Error(error.message);
  }
};

// Remove car
export const removeCar = async (uid: string, carId: string): Promise<void> => {
  try {
    const userDoc = await getDoc(doc(db, 'users', uid));
    
    if (!userDoc.exists()) {
      throw new Error('User not found');
    }

    const userData = userDoc.data() as UserData;
    const updatedCars = userData.cars.filter(car => car.id !== carId);

    await updateUserData(uid, { cars: updatedCars });
  } catch (error: any) {
    throw new Error(error.message);
  }
};

// Reset password
export const resetPassword = async (email: string): Promise<void> => {
  try {
    await sendPasswordResetEmail(auth, email);
  } catch (error: any) {
    throw new Error(error.message);
  }
};

// Auth state listener
export const onAuthStateChange = (callback: (user: User | null) => void) => {
  return onAuthStateChanged(auth, callback);
};

